import React, { useEffect, useRef, useState } from 'react';
import { CommentAvatar } from './CommentAvatar';
import { IonButton, IonTextarea } from '@ionic/react';
import { IonTextareaCustomEvent, TextareaChangeEventDetail } from '@ionic/core';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { UserState } from '../user/UserState';
import { useAutoFocus } from '../utils/useAutoFocus';
import { useSubscription } from '@meraki-internal/state';
import { CommentsModelFactory } from './CommentsModelFactory';
import { ICaremapNote } from '../notes/model/NotesClient';
import { usePlaceCursor } from '../utils/usePlaceCursor';
import { useIfLoggedIn } from '../utils/useIfLoggedIn';
import { AlertPresenter } from '../AlertPresenter';
import { INoteInteractionSurface } from '../notes/reactions/INoteInteractionSurface';
import { decodeHtmlEntities } from '../utils/decodeHtmlEntities';

const useStyles = createUseStyles({
    notesTextArea: {
        marginLeft: 12,
        minHeight: 'unset !important',

        '--border-radius': '10px !important',
        '--background': 'rgba(var(--ion-text-color-rgb, 0, 0, 0), 0.07)',
        // 0 normal border, and important for focus border
        '--border-width': '0px !important',
        '--padding-top': '8px',
        '--padding-bottom': '8px',

        '& textarea': {
            paddingInlineStart: 12,
            paddingInlineEnd: 12
        }
    },
});

export const CommentEditor: React.FC<{
    note: ICaremapNote;
    onNotFound: () => void;
    autoFocus?: boolean;
    surface: INoteInteractionSurface;
    authenticatedRedirect: string;
}> = ({ note, onNotFound, autoFocus, surface, authenticatedRedirect }) => {
    const classes = useStyles();

    const alert = useInstance(AlertPresenter);

    const fieldRef = useRef<HTMLIonTextareaElement>(null);
    const focusCommentField = useAutoFocus(fieldRef);

    useEffect(() => {
        if (autoFocus) {
            focusCommentField();
        }
    }, [autoFocus]); // eslint-disable-line react-hooks/exhaustive-deps

    const factory = useInstance(CommentsModelFactory);
    useSubscription(() => factory);
    const model = factory.forNote(note);

    const comment = model.getEditingComment();
    // decoded once from the saved comment
    const [html, setHtml] = useState(decodeHtmlEntities(comment?.html || ''));

    const placeCursor = usePlaceCursor();
    useEffect(() => {
        // if editing a comment, focus and put cursor at the end
        if (comment?.html) {
            placeCursor(fieldRef);
        }
        setHtml(decodeHtmlEntities(comment?.html || ''));
    }, [comment]); // eslint-disable-line react-hooks/exhaustive-deps

    // stop comment editing when unmounting
    useEffect(() => {
        return () => {
            model.doneEditing();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const userState = useInstance(UserState);
    useSubscription(() => userState);

    const commentChanged = (e: IonTextareaCustomEvent<TextareaChangeEventDetail>) => {
        setHtml(e.detail.value || '');
    };

    const ifLoggedIn = useIfLoggedIn();
    const commentFocused = (e: React.FocusEvent<HTMLIonTextareaElement>) => {
        ifLoggedIn({
            delegate: () => {},
            unathenticatedDelegate: () => (e.nativeEvent?.target as HTMLIonTextareaElement).blur(),
            message: 'Please sign in or create an account to add comments.',
            redirect: authenticatedRedirect,
            trackingFrom: 'Comments'
        });
    };

    const handleSave = async () => {
        try {
            if (comment) {
                await model.saveEditing({ comment: { ...comment, html }, surface });
            } else {
                await model.addNewComment({ html, surface });
            }
            setHtml('');
        } catch (e: any) {
            if (e.status === 404) {
                model.doneEditing();
                onNotFound();
                alert.showAlertV2({
                    header: 'Deleted Note',
                    message: `We're sorry, this note has been deleted.`
                });
            } else {
                alert.showAndLogError(e);
            }
        }
    };

    const userFirstName = userState.state.firstName;
    // an existing comment shows its author, even where their details are missing; a new one shows you
    const author = comment ? comment.createdByUser : userState.state;

    return (
        <div style={{display: 'flex', alignItems: 'center'}}>
            <div>
                <CommentAvatar size={36} author={author} />
            </div>
            <IonTextarea
                autoGrow
                rows={1}
                value={html}
                ref={fieldRef}
                fill="outline"
                data-id="comment-editor"
                onIonInput={commentChanged}
                onFocus={commentFocused}
                className={classes.notesTextArea}
                // replay records attribute values even under mp-mask, so replays show this first name; kept by choice
                placeholder={model.canAdd() ? `${userFirstName ? `Comment as ${userFirstName}` : 'Add a comment'}` : 'Add a comment'}
            />
            <IonButton
                fill="clear"
                disabled={!html}
                data-id="post-comment-btn"
                onClick={handleSave}>Post</IonButton>
        </div>
    );
};
